/**
 * SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { ColumnTypes } from './columnHandler.js'
import { translate as t } from '@nextcloud/l10n'

class BaseFilter {

	constructor() {
		this.icon = 'icon-add'
		this.source = 'operators'
		this.subline = t('tables', 'Filter operator')
	}

}

export class Filter extends BaseFilter {

	constructor({ id, label, goodFor, incompatibleWith, shortLabel = null, noSearchValue = false } = {}) {
		super()
		this.id = id
		this.label = label
		this.shortLabel = shortLabel
		this.goodFor = goodFor
		this.incompatibleWith = incompatibleWith
		this.noSearchValue = noSearchValue
	}

	getOperatorLabel() {
		return this.shortLabel ?? this.label
	}

}

export function getFilterWithId(id) {
	return Object.values(Filters).find(fil => fil.id === id)
}

export const FilterIds = {
	Contains: 'contains',
	ContainsItem: 'contains-item',
	DoesNotContain: 'does-not-contain',
	BeginsWith: 'begins-with',
	EndsWith: 'ends-with',
	IsEqual: 'is-equal',
	IsNotEqual: 'is-not-equal',
	IsGreaterThan: 'is-greater-than',
	IsGreaterThanOrEqual: 'is-greater-than-or-equal',
	IsLowerThan: 'is-lower-than',
	IsLowerThanOrEqual: 'is-lower-than-or-equal',
	IsEmpty: 'is-empty',
	IsNotEmpty: 'is-not-empty',
}

export const Filters = {
	ContainsItem: new Filter({
		id: FilterIds.ContainsItem,
		label: t('tables', 'Contains items'),
		goodFor: [ColumnTypes.SelectionMulti, ColumnTypes.Selection],
		incompatibleWith: [FilterIds.DoesNotContain, FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual],
	}),
	Contains: new Filter({
		id: FilterIds.Contains,
		label: t('tables', 'Contains'),
		goodFor: [ColumnTypes.TextLine, ColumnTypes.TextLong, ColumnTypes.TextLink, ColumnTypes.TextRich, ColumnTypes.SelectionMulti, ColumnTypes.Usergroup, ColumnTypes.Selection, ColumnTypes.Relation],
		incompatibleWith: [FilterIds.DoesNotContain, FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual],
	}),
	DoesNotContain: new Filter({
		id: FilterIds.DoesNotContain,
		label: t('tables', 'Does not contain'),
		goodFor: [ColumnTypes.TextLine, ColumnTypes.TextLong, ColumnTypes.TextLink, ColumnTypes.TextRich, ColumnTypes.SelectionMulti, ColumnTypes.Usergroup, ColumnTypes.Selection, ColumnTypes.Relation],
		incompatibleWith: [FilterIds.Contains, FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual],
	}),
	BeginsWith: new Filter({
		id: FilterIds.BeginsWith,
		label: t('tables', 'Begins with'),
		goodFor: [ColumnTypes.TextLine, ColumnTypes.TextLink],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.BeginsWith],
	}),
	EndsWith: new Filter({
		id: FilterIds.EndsWith,
		label: t('tables', 'Ends with'),
		goodFor: [ColumnTypes.TextLine, ColumnTypes.TextLink],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.EndsWith],
	}),
	IsEqual: new Filter({
		id: FilterIds.IsEqual,
		label: t('tables', 'Is equal'),
		shortLabel: '=',
		goodFor: [ColumnTypes.TextLine, ColumnTypes.Number, ColumnTypes.SelectionCheck, ColumnTypes.TextLink, ColumnTypes.NumberStars, ColumnTypes.NumberProgress, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime, ColumnTypes.Selection, ColumnTypes.SelectionMulti, ColumnTypes.Usergroup, ColumnTypes.Relation],
		incompatibleWith: [FilterIds.IsNotEqual, FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.BeginsWith, FilterIds.EndsWith, FilterIds.Contains, FilterIds.IsGreaterThan, FilterIds.IsGreaterThanOrEqual, FilterIds.IsLowerThan, FilterIds.IsLowerThanOrEqual],
	}),
	IsNotEqual: new Filter({
		id: FilterIds.IsNotEqual,
		label: t('tables', 'Is not equal'),
		shortLabel: '!=',
		goodFor: [ColumnTypes.TextLine, ColumnTypes.Number, ColumnTypes.SelectionCheck, ColumnTypes.TextLink, ColumnTypes.NumberStars, ColumnTypes.NumberProgress, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime, ColumnTypes.Selection, ColumnTypes.SelectionMulti, ColumnTypes.Usergroup, ColumnTypes.Relation],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.BeginsWith, FilterIds.EndsWith, FilterIds.Contains, FilterIds.IsGreaterThan, FilterIds.IsGreaterThanOrEqual, FilterIds.IsLowerThan, FilterIds.IsLowerThanOrEqual],
	}),
	IsGreaterThan: new Filter({
		id: FilterIds.IsGreaterThan,
		label: t('tables', 'Is greater than'),
		shortLabel: '>',
		goodFor: [ColumnTypes.Number, ColumnTypes.NumberStars, ColumnTypes.NumberProgress, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.IsGreaterThan, FilterIds.IsGreaterThanOrEqual],
	}),
	IsGreaterThanOrEqual: new Filter({
		id: FilterIds.IsGreaterThanOrEqual,
		label: t('tables', 'Is greater than or equal'),
		shortLabel: '>=',
		goodFor: [ColumnTypes.Number, ColumnTypes.NumberStars, ColumnTypes.NumberProgress, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.IsGreaterThan, FilterIds.IsGreaterThanOrEqual],
	}),
	IsLowerThan: new Filter({
		id: FilterIds.IsLowerThan,
		label: t('tables', 'Is lower than'),
		shortLabel: '<',
		goodFor: [ColumnTypes.Number, ColumnTypes.NumberStars, ColumnTypes.NumberProgress, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.IsLowerThan, FilterIds.IsLowerThanOrEqual],
	}),
	IsLowerThanOrEqual: new Filter({
		id: FilterIds.IsLowerThanOrEqual,
		label: t('tables', 'Is lower than or equal'),
		shortLabel: '<=',
		goodFor: [ColumnTypes.Number, ColumnTypes.NumberStars, ColumnTypes.NumberProgress, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime],
		incompatibleWith: [FilterIds.IsEmpty, FilterIds.IsNotEmpty, FilterIds.IsEqual, FilterIds.IsLowerThan, FilterIds.IsLowerThanOrEqual],
	}),
	IsEmpty: new Filter({
		id: FilterIds.IsEmpty,
		label: t('tables', 'Is empty'),
		goodFor: [ColumnTypes.TextLine, ColumnTypes.TextRich, ColumnTypes.Number, ColumnTypes.TextLink, ColumnTypes.NumberProgress, ColumnTypes.NumberStars, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime, ColumnTypes.Selection, ColumnTypes.SelectionMulti, ColumnTypes.SelectionCheck, ColumnTypes.TextLong, ColumnTypes.Usergroup, ColumnTypes.Relation],
		incompatibleWith: [FilterIds.Contains, FilterIds.BeginsWith, FilterIds.EndsWith, FilterIds.IsEqual, FilterIds.IsGreaterThan, FilterIds.IsGreaterThanOrEqual, FilterIds.IsLowerThan, FilterIds.IsLowerThanOrEqual, FilterIds.IsEmpty, FilterIds.IsNotEmpty],
		noSearchValue: true,
	}),
	IsNotEmpty: new Filter({
		id: FilterIds.IsNotEmpty,
		label: t('tables', 'Is not empty'),
		goodFor: [ColumnTypes.TextLine, ColumnTypes.TextRich, ColumnTypes.Number, ColumnTypes.TextLink, ColumnTypes.NumberProgress, ColumnTypes.NumberStars, ColumnTypes.DatetimeDate, ColumnTypes.DatetimeTime, ColumnTypes.Datetime, ColumnTypes.Selection, ColumnTypes.SelectionMulti, ColumnTypes.TextLong, ColumnTypes.Usergroup, ColumnTypes.Relation],
		incompatibleWith: [FilterIds.Contains, FilterIds.BeginsWith, FilterIds.EndsWith, FilterIds.IsEqual, FilterIds.IsGreaterThan, FilterIds.IsGreaterThanOrEqual, FilterIds.IsLowerThan, FilterIds.IsLowerThanOrEqual, FilterIds.IsEmpty, FilterIds.IsNotEmpty],
		noSearchValue: true,
	}),
}

export function getFiltersForColumn(column, viewSetting) {
	const filters = viewSetting?.filter?.filter(filter => {
		if (filter.columnId !== column.id) {
			return false
		}

		if (filter.operator.id === FilterIds.ContainsItem) {
			return filter.value.length > 0
		}

		return true
	})
	return filters?.length > 0 ? filters : []
}
